/**
 * Super Admin API Client
 */

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8787';

/**
 * 取得 Super Admin Token（從 localStorage）
 */
function getToken(): string | null {
  return localStorage.getItem('super_admin_token');
}

/**
 * 設定 Super Admin Token（存入 localStorage）
 */
export function setToken(token: string): void {
  localStorage.setItem('super_admin_token', token);
}

/**
 * 清除 Super Admin Token
 */
export function clearToken(): void {
  localStorage.removeItem('super_admin_token');
}

/**
 * 發送 API 請求（自動附加 Super Admin Token）
 */
async function fetchWithAuth(
  endpoint: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getToken();
  const headers = new Headers(options.headers);

  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  headers.set('Content-Type', 'application/json');

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  // 如果 Token 過期或無效，清除並重定向到登入頁
  if (response.status === 401) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  return response;
}

/**
 * GET 請求
 */
export async function get<T>(endpoint: string): Promise<T> {
  const response = await fetchWithAuth(endpoint, {
    method: 'GET',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Request failed');
  }

  return response.json();
}

/**
 * POST 請求
 */
export async function post<T>(endpoint: string, body?: any): Promise<T> {
  const response = await fetchWithAuth(endpoint, {
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Request failed');
  }

  return response.json();
}

/**
 * PATCH 請求
 */
export async function patch<T>(endpoint: string, body?: any): Promise<T> {
  const response = await fetchWithAuth(endpoint, {
    method: 'PATCH',
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Request failed');
  }

  return response.json();
}

/**
 * DELETE 請求
 */
export async function del<T>(endpoint: string): Promise<T> {
  const response = await fetchWithAuth(endpoint, {
    method: 'DELETE',
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Request failed');
  }

  return response.json();
}

/**
 * 上傳圖片（multipart），回傳 { url }
 */
export async function uploadImage(
  endpoint: string,
  file: File
): Promise<{ url: string }> {
  const token = getToken();
  const form = new FormData();
  form.append('file', file);
  const headers: HeadersInit = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_BASE}${endpoint}`, {
    method: 'POST',
    body: form,
    headers,
  });
  if (res.status === 401) {
    clearToken();
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }
  const data = (await res.json()) as {
    success?: boolean;
    url?: string;
    message?: string;
  };
  if (!res.ok)
    throw new Error((data as { message?: string }).message || '上傳失敗');
  if (data.url) return { url: data.url };
  throw new Error('上傳回應缺少 url');
}
